import { useState, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Checkbox,
  Paper,
  Popover,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Skeleton,
} from "@mui/material";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useSelector } from "react-redux";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Wrapper } from "../../components";
import { apiAnalyticsGrouped } from "../../services";

const SkeletonRows = ({ rows = 5, cols = 7 }) => (
  <>
    {Array.from({ length: rows }).map((_, i) => (
      <TableRow key={i}>
        {Array.from({ length: cols }).map((_, j) => (
          <TableCell key={j}><Skeleton width={80} /></TableCell>
        ))}
      </TableRow>
    ))}
  </>
);

const CONTROL_HEIGHT = 38;

const FilterDropdown = ({ label, selectLabel, options, selected, onChange }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const toggle = (value) => {
    onChange(
      selected.includes(value)
        ? selected.filter((v) => v !== value)
        : [...selected, value]
    );
  };

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<FilterAltOutlinedIcon sx={{ fontSize: 18 }} />}
        endIcon={open ? <KeyboardArrowUpIcon sx={{ fontSize: 18 }} /> : <KeyboardArrowDownIcon sx={{ fontSize: 18 }} />}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          height: CONTROL_HEIGHT,
          textTransform: "none",
          borderRadius: 3,
          px: 2,
          fontSize: "14px",
          color: "#0f172a",
          borderColor: selected.length > 0 ? "#2563EB" : "#e2e8f0",
          backgroundColor: "#fff",
        }}
      >
        {label}
      </Button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ sx: { borderRadius: 3, mt: 1, minWidth: 220, p: 1 } }}
      >
        <Typography sx={{ px: 1, pt: 0.5, pb: 1, fontSize: "14px", fontWeight: 600 }}>
          {selectLabel}
        </Typography>
        {options.map((opt) => (
          <Box
            key={opt}
            onClick={() => toggle(opt)}
            sx={{
              display: "flex",
              alignItems: "center",
              px: 1,
              py: 0.5,
              cursor: "pointer",
              borderRadius: 1,
              "&:hover": { bgcolor: "#F8FAFC" },
            }}
          >
            <Checkbox checked={selected.includes(opt)} size="small" sx={{ p: 0, mr: 1 }} />
            <Typography sx={{ fontSize: "14px" }}>{String(opt)}</Typography>
          </Box>
        ))}
      </Popover>
    </>
  );
};

const getStatus = (row) => {
  if (Number(row.success_rate) === 100) return "Success";
  if (Number(row.failure_count) > 0 && Number(row.success_count) === 0) return "Failed";
  return "Partial";
};

const getStatusColor = (status) => {
  if (status === "Success") return "#16A34A";
  if (status === "Failed") return "#d32f2f";
  return "#F97316";
};

export function ApiAnalyticsGrouped() {
  const navigate = useNavigate();
  const { companyName } = useParams();
  const [searchParams] = useSearchParams();
  const date = searchParams.get("date");
  const apiName = searchParams.get("apiName");
  const decodedCompanyName = companyName ? decodeURIComponent(companyName) : null;
  const { user } = useSelector((state) => state.auth);
  const jwtToken = user?.jwt;

  const [data, setData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApiTypes, setSelectedApiTypes] = useState([]);
  const [selectedStatusCodes, setSelectedStatusCodes] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);

  useEffect(() => {
    if (!jwtToken || !decodedCompanyName || !date || !apiName) return;

    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await apiAnalyticsGrouped(jwtToken, decodedCompanyName, date, date, apiName);
        if (res?.status === 200) {
          const rows = res.data?.data || [];
          setData(rows);
          if (rows.length > 0) {
            setColumns(Object.keys(rows[0]));
          }
        }
      } catch (error) {
        console.error("Failed to fetch grouped analytics:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [jwtToken, decodedCompanyName, date, apiName]);

  const formatHeader = (key) => {
    return key
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const apiNameOptions = useMemo(
    () => [...new Set(data.map((r) => r.api_name).filter(Boolean))],
    [data]
  );

  const statusOptions = useMemo(
    () => [...new Set(data.map((r) => getStatus(r)))],
    [data]
  );

  const filteredData = useMemo(() => {
    return data.filter((row) => {
      if (selectedApiTypes.length > 0 && !selectedApiTypes.includes(row.api_name)) return false;
      if (selectedStatusCodes.length > 0 && !selectedStatusCodes.includes(getStatus(row))) return false;
      return true;
    });
  }, [data, selectedApiTypes, selectedStatusCodes]);

  const paginatedData = filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const headerSx = { fontSize: "14px", fontWeight: 600, color: "#64748B", textAlign: "center" };
  const cellSx = { fontSize: "14px", textAlign: "center" };

  const renderCellValue = (key, value, row) => {
    if (key === "api_name") {
      return (
        <Typography sx={{ color: "#2563EB", fontWeight: 500, fontSize: "14px" }}>
          {value}
        </Typography>
      );
    }
    if (key === "success_rate") return `${value}%`;
    if (key === "status") {
      const status = getStatus(row);
      return (
        <Typography sx={{ color: getStatusColor(status), fontWeight: 500, fontSize: "14px" }}>
          {status}
        </Typography>
      );
    }
    return value;
  };

  const displayColumns = columns.length > 0 ? columns : [];
  const hasStatusInData = columns.includes("status");
  const allColumns = hasStatusInData ? displayColumns : [...displayColumns, "status"];

  return (
    <Wrapper>
      <Stack sx={{ pt: 2, mt: 7 }} spacing={2} className="wrapper">
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box
            onClick={() => navigate(-1)}
            sx={{ display: "inline-flex", alignItems: "center", gap: 1, cursor: "pointer" }}
          >
            <ArrowBackIcon sx={{ fontSize: 22, color: "#475569" }} />
            <Typography variant="h4" fontWeight="bold">
              View Logs
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 1 }}>
            <FilterDropdown
              label="API Type"
              selectLabel="Select API Types"
              options={apiNameOptions}
              selected={selectedApiTypes}
              onChange={setSelectedApiTypes}
            />
            <FilterDropdown
              label="Status Code"
              selectLabel="Select Status"
              options={statusOptions}
              selected={selectedStatusCodes}
              onChange={setSelectedStatusCodes}
            />
          </Box>
        </Box>

        <Paper sx={{ width: "100%", borderRadius: 3, boxShadow: 2, p: 2 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {allColumns.map((col) => (
                    <TableCell key={col} sx={headerSx}>
                      {formatHeader(col)}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <SkeletonRows cols={allColumns.length || 7} />
                ) : paginatedData.length > 0 ? (
                  paginatedData.map((row, index) => {
                    const status = getStatus(row);
                    return (
                      <TableRow key={index}>
                        {allColumns.map((col) => (
                          <TableCell key={col} sx={cellSx}>
                            {col === "status" && !hasStatusInData ? (
                              <Typography sx={{ color: getStatusColor(status), fontWeight: 500, fontSize: "14px" }}>
                                {status}
                              </Typography>
                            ) : (
                              renderCellValue(col, row[col], row)
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={allColumns.length || 7} align="center" sx={{ py: 4, fontSize: "15px" }}>
                      No data found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={filteredData.length}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(Number(e.target.value));
              setPage(0);
            }}
            rowsPerPageOptions={[15, 25, 50, 100]}
            sx={{
              mt: 2,
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                fontSize: "12px",
                fontWeight: 600,
              },
              "& .MuiTablePagination-select": { fontSize: "12px" },
            }}
          />
        </Paper>
      </Stack>
    </Wrapper>
  );
}
